import React, { useEffect, useMemo, useState } from 'react';
import {
  Bell,
  BellOff,
  CalendarDays,
  Check,
  CheckCheck,
  CheckCircle,
  Clock,
  Download,
  ExternalLink,
  FileText,
  Image as ImageIcon,
  Link as LinkIcon,
  Loader2,
  MapPin,
  Music,
  Search,
  UserPlus,
  Users,
  Video
} from 'lucide-react';
import apiService from '../../services/apiService';

const MONTHS_SHORT = ['JAN', 'FÉV', 'MAR', 'AVR', 'MAI', 'JUIN', 'JUIL', 'AOÛT', 'SEP', 'OCT', 'NOV', 'DÉC'];

const AVATAR_COLORS = [
  'bg-indigo-500', 'bg-purple-500', 'bg-pink-500', 'bg-emerald-500',
  'bg-sky-500', 'bg-amber-500', 'bg-rose-500', 'bg-teal-500'
];

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('fr-FR');
};

// Une date "AAAA-MM-JJ" est lue comme date locale pour éviter le décalage de fuseau
const parseDay = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value || '');
  return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : null;
};

const EmptyState = ({ icon: Icon, title, text }) => (
  <div className="flex flex-col items-center text-center py-10 px-4">
    <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-3">
      <Icon className="w-7 h-7 text-gray-400" />
    </div>
    <p className="font-medium text-gray-800">{title}</p>
    {text && <p className="text-sm text-gray-500 mt-1">{text}</p>}
  </div>
);

const SectionHeader = ({ title, subtitle, children }) => (
  <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
    <div className="min-w-0">
      <h2 className="font-semibold text-gray-900 break-words">{title}</h2>
      {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
    </div>
    {children}
  </div>
);

export const FraternityTab = ({ fraternity, zone, members }) => {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return members;
    return members.filter((m) =>
      `${m.first_name} ${m.last_name} ${m.couple_name || ''}`.toLowerCase().includes(term)
    );
  }, [members, search]);

  return (
    <section className="bg-white rounded-xl shadow p-4 sm:p-5">
      <SectionHeader
        title={fraternity || 'Ma fraternité'}
        subtitle={`${zone ? `Zone ${zone} · ` : ''}${members.length} autre(s) membre(s)`}
      >
        {members.length > 6 && (
          <div className="relative w-full sm:w-64">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un membre"
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
        )}
      </SectionHeader>

      {members.length === 0 ? (
        <EmptyState icon={Users} title="Aucun autre membre" text="Les membres de votre fraternité apparaîtront ici." />
      ) : filtered.length === 0 ? (
        <EmptyState icon={Search} title="Aucun résultat" />
      ) : (
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((member) => {
            const initials = `${member.first_name?.[0] || ''}${member.last_name?.[0] || ''}`.toUpperCase();
            return (
              <li key={member.id} className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 bg-gray-50/60">
                <span
                  className={`shrink-0 w-11 h-11 rounded-full flex items-center justify-center text-white text-sm font-semibold ${AVATAR_COLORS[member.id % AVATAR_COLORS.length]}`}
                >
                  {initials}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-900 truncate">{member.first_name} {member.last_name}</p>
                  <p className="text-xs text-gray-500 truncate">
                    {member.couple_name ? `Couple ${member.couple_name}` : 'Membre'}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

const REGISTRATION_LABELS = {
  pending: 'Inscription envoyée · en attente de confirmation',
  confirmed: 'Inscription confirmée'
};

const EventRegistrationAction = ({ event, onChanged }) => {
  const [formOpen, setFormOpen] = useState(false);
  const [count, setCount] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const registration = event.registration;
  const maxCount = Math.min(10, event.spots_left ?? 10);

  const run = async (action) => {
    setBusy(true);
    setError('');
    try {
      await action();
      setFormOpen(false);
      await onChanged();
    } catch (err) {
      setError(err.message || 'Une erreur est survenue');
    } finally {
      setBusy(false);
    }
  };

  if (registration) {
    return (
      <div className="mt-3 rounded-lg bg-green-50 border border-green-200 p-3">
        <p className="flex items-center gap-1.5 text-xs font-medium text-green-700">
          <CheckCircle className="w-4 h-4 shrink-0" />
          {REGISTRATION_LABELS[registration.status] || 'Inscrit'}
          {registration.participants_count > 1 && ` (${registration.participants_count} pers.)`}
        </p>
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            if (window.confirm('Annuler votre inscription à cet événement ?')) {
              run(() => apiService.delete(`/member/events/${event.id}/register`));
            }
          }}
          className="mt-2 text-xs font-medium text-red-600 hover:text-red-700 disabled:opacity-60"
        >
          {busy ? 'Annulation…' : 'Annuler mon inscription'}
        </button>
        {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
      </div>
    );
  }

  if (event.spots_left === 0) {
    return <p className="mt-3 text-xs font-medium text-red-600">Événement complet</p>;
  }

  return (
    <div className="mt-3">
      {!formOpen ? (
        <button
          type="button"
          onClick={() => setFormOpen(true)}
          className="flex items-center justify-center gap-1.5 w-full sm:w-auto px-4 py-2 text-sm font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
        >
          <UserPlus className="w-4 h-4" /> S'inscrire
        </button>
      ) : (
        <div className="rounded-lg border border-indigo-200 bg-indigo-50/50 p-3">
          <label className="block text-xs font-medium text-gray-700 mb-1" htmlFor={`count-${event.id}`}>
            Nombre de participants (vous inclus)
          </label>
          <select
            id={`count-${event.id}`}
            value={count}
            onChange={(e) => setCount(Number(e.target.value))}
            className="border border-gray-300 rounded-lg text-sm px-2 py-1.5 bg-white"
          >
            {Array.from({ length: maxCount }, (_, i) => i + 1).map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => run(() => apiService.post(`/member/events/${event.id}/register`, { participants_count: count }))}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Confirmer l'inscription
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setFormOpen(false)}
              className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 text-gray-700 hover:bg-white"
            >
              Annuler
            </button>
          </div>
          {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
        </div>
      )}
      {!formOpen && error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
};

export const EventsTab = ({ events, onChanged }) => (
  <section className="bg-white rounded-xl shadow p-4 sm:p-5">
    <SectionHeader title="Événements à venir" subtitle={`${events.length} événement(s)`} />

    {events.length === 0 ? (
      <EmptyState icon={CalendarDays} title="Aucun événement à venir" text="Les prochains événements de la plateforme apparaîtront ici." />
    ) : (
      <ul className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {events.map((event) => {
          const day = parseDay(event.date);
          return (
            <li key={event.id} className="flex gap-3 p-3 rounded-xl border border-gray-100">
              <div className="shrink-0 w-14 h-16 rounded-lg bg-indigo-50 text-indigo-700 flex flex-col items-center justify-center">
                <span className="text-xl font-bold leading-none">{day ? day.getDate() : '—'}</span>
                <span className="text-[11px] font-medium mt-1">{day ? MONTHS_SHORT[day.getMonth()] : ''}</span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900 break-words">{event.title}</p>
                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500">
                  {event.time && <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{String(event.time).slice(0, 5)}</span>}
                  {event.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{event.location}</span>}
                </div>
                {event.description && (
                  <p className="mt-2 text-xs text-gray-600 line-clamp-3 whitespace-pre-line">{event.description}</p>
                )}
                <div className="mt-2 flex flex-wrap gap-2">
                  {event.category && (
                    <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[11px] capitalize">{event.category}</span>
                  )}
                  {event.registration_required ? (
                    <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[11px]">Inscription requise</span>
                  ) : null}
                  {event.registration_required && event.spots_left !== null && event.spots_left !== undefined && event.spots_left > 0 ? (
                    <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[11px]">{event.spots_left} place(s) restante(s)</span>
                  ) : null}
                </div>
                {event.registration_required ? <EventRegistrationAction event={event} onChanged={onChanged} /> : null}
              </div>
            </li>
          );
        })}
      </ul>
    )}
  </section>
);

const TYPE_ICONS = { document: FileText, audio: Music, video: Video, image: ImageIcon, link: LinkIcon };

export const ResourcesTab = () => {
  const [resources, setResources] = useState(null);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    apiService.get('/member/resources')
      .then((response) => !cancelled && setResources(response.data || []))
      .catch((err) => !cancelled && setError(err.message || 'Impossible de charger les ressources'));
    return () => { cancelled = true; };
  }, []);

  const openResource = async (resource) => {
    if (resource.external_url) {
      window.open(resource.external_url, '_blank', 'noopener');
      return;
    }
    setBusyId(resource.id);
    setError('');
    try {
      const { blob, fileName } = await apiService.downloadFile(`/public/resources/${resource.id}/download`);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || resource.file_name || resource.title;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      setError(err.message || 'Téléchargement impossible');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className="bg-white rounded-xl shadow p-4 sm:p-5">
      <SectionHeader title="Ressources" subtitle="Documents, médias et liens mis à disposition des membres" />

      {error && <div className="mb-3 bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}

      {resources === null && !error ? (
        <div className="py-10 flex justify-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : resources && resources.length === 0 ? (
        <EmptyState icon={FileText} title="Aucune ressource disponible" />
      ) : (
        <ul className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {(resources || []).map((resource) => {
            const Icon = TYPE_ICONS[resource.type] || FileText;
            const isLink = Boolean(resource.external_url);
            return (
              <li key={resource.id} className="flex items-start gap-3 p-3 rounded-xl border border-gray-100">
                <span className="shrink-0 w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
                  <Icon className="w-5 h-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-900 break-words">{resource.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {[resource.category_label || resource.category, resource.author, resource.file_size_human].filter(Boolean).join(' · ')}
                  </p>
                  {resource.description && <p className="mt-1 text-xs text-gray-600 line-clamp-2">{resource.description}</p>}
                  {resource.is_member && !resource.is_public && (
                    <span className="inline-block mt-2 px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-[11px]">Réservé aux membres</span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => openResource(resource)}
                  disabled={busyId === resource.id || (!isLink && !resource.has_file)}
                  aria-label={isLink ? 'Ouvrir' : 'Télécharger'}
                  className="shrink-0 w-9 h-9 rounded-lg bg-indigo-600 text-white flex items-center justify-center hover:bg-indigo-700 disabled:opacity-40"
                >
                  {busyId === resource.id ? <Loader2 className="w-4 h-4 animate-spin" /> : isLink ? <ExternalLink className="w-4 h-4" /> : <Download className="w-4 h-4" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export const NotificationsTab = ({ notifications, onMarkRead, onMarkAllRead }) => {
  const [openId, setOpenId] = useState(null);
  const [pendingId, setPendingId] = useState(null);
  const unreadCount = notifications.filter((n) => !n.read).length;

  const run = async (key, action) => {
    setPendingId(key);
    try {
      await action();
    } finally {
      setPendingId(null);
    }
  };

  return (
    <section className="bg-white rounded-xl shadow p-4 sm:p-5">
      <SectionHeader
        title="Notifications"
        subtitle={unreadCount > 0 ? `${unreadCount} non lue(s)` : 'Tout est à jour'}
      >
        {unreadCount > 0 && (
          <button
            type="button"
            onClick={() => run('all', onMarkAllRead)}
            disabled={pendingId === 'all'}
            className="flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-800 disabled:opacity-60"
          >
            {pendingId === 'all' ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCheck className="w-4 h-4" />}
            Tout marquer comme lu
          </button>
        )}
      </SectionHeader>

      {notifications.length === 0 ? (
        <EmptyState icon={BellOff} title="Aucune notification" />
      ) : (
        <ul className="space-y-3">
          {notifications.map((notification) => {
            const isOpen = openId === notification.id;
            return (
              <li
                key={notification.id}
                className={`rounded-xl border p-3 sm:p-4 ${
                  notification.read ? 'border-gray-100 bg-white' : 'border-indigo-200 bg-indigo-50/50'
                }`}
              >
                <div className="flex items-start gap-3">
                  <span
                    className={`shrink-0 mt-0.5 w-9 h-9 rounded-full flex items-center justify-center ${
                      notification.read ? 'bg-gray-100 text-gray-400' : 'bg-indigo-100 text-indigo-600'
                    }`}
                  >
                    <Bell className="w-4 h-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1">
                      <p className={`text-sm break-words ${notification.read ? 'text-gray-700' : 'font-semibold text-gray-900'}`}>
                        {notification.subject}
                      </p>
                      <span className="text-xs text-gray-400 whitespace-nowrap">{formatDate(notification.date)}</span>
                    </div>
                    <p className={`mt-1 text-sm text-gray-600 whitespace-pre-line ${isOpen ? '' : 'line-clamp-2'}`}>
                      {isOpen ? notification.body : notification.excerpt}
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setOpenId(isOpen ? null : notification.id)}
                        className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
                      >
                        {isOpen ? 'Réduire' : 'Lire'}
                      </button>
                      {notification.read ? (
                        <span className="flex items-center gap-1 text-xs text-gray-400"><Check className="w-3.5 h-3.5" /> Lue</span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => run(notification.id, () => onMarkRead(notification.id))}
                          disabled={pendingId === notification.id}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60"
                        >
                          {pendingId === notification.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                          Marquer comme lu
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};
